import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
    Users,
    MessageSquare,
    CreditCard,
    Activity,
    ChevronLeft,
    ChevronRight,
    LogOut,
    Shield,
    Sun,
    Moon
} from "lucide-react";
import { useTheme } from "../../../../context/ThemeContext";
import { useAuth } from "../../../../context/AuthContext";

const Sidebar = () => {
    const { isDarkTheme, toggleTheme } = useTheme();
    const { logout } = useAuth();
    const navigate = useNavigate();

    const [collapsed, setCollapsed] = useState(window.innerWidth < 768);
    const [hoverItem, setHoverItem] = useState(null);

    // Listen to resize events to update sidebar state
    useEffect(() => {
        const handleResize = () => {
            setCollapsed(window.innerWidth < 768);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const toggleSidebar = () => {
        setCollapsed(!collapsed);
    };

    const handleLogout = () => {
        logout();
        navigate("/auth");
    };

    const menuItems = [
        {
            path: "/admin",
            name: "Users",
            icon: <Users size={20} />,
        },
        {
            path: "/admin/feedback",
            name: "Feedback",
            icon: <MessageSquare size={20} />,
        },
        {
            path: "/admin/statistics",
            name: "Statistics",
            icon: <CreditCard size={20} />,
        },
        {
            path: "/admin/activity-logs",
            name: "Activity Logs",
            icon: <Activity size={20} />,
        },
    ];

    return (
        <div
            className={`${collapsed ? "w-16 md:w-20" : "w-64"} transition-all duration-500 ease-in-out h-screen ${isDarkTheme ? "bg-gray-900" : "bg-gray-50"
                } ${isDarkTheme ? "border-gray-800" : "border-gray-200"} border-r shadow-lg flex flex-col pt-4 relative`}
        >
            {/* Decorative element - top right corner accent */}
            <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 opacity-20 rounded-bl-full pointer-events-none"></div>

            {/* Header */}
            <div className="flex flex-col items-center justify-center p-4">
                {!collapsed ? (
                    <div className="flex items-center justify-between w-full ml-24">
                        <div className="flex flex-col">
                            <div className="flex items-center">
                                <h1 onClick={() => navigate("/")} className="cursor-pointer text-2xl font-bold">
                                    <span className="bg-gradient-to-r from-amber-400 to-amber-600 bg-clip-text text-transparent">
                                        OG
                                    </span>
                                    <span className={isDarkTheme ? "text-white" : "text-gray-800"}>-FLEET</span>
                                </h1>
                                {/* Toggle Button inline with the header for expanded view */}
                                <button
                                    onClick={toggleSidebar}
                                    className={`ml-9 rounded-full mt-1 p-3 transition-all duration-200 ease-in-out ${isDarkTheme
                                        ? "bg-gray-800 text-amber-400 hover:bg-gray-700"
                                        : "bg-gray-200 text-amber-500 hover:bg-gray-300"
                                        } hover:shadow-md`}
                                >
                                    <ChevronLeft size={18} />
                                </button>
                            </div>
                            <p className={`text-xs font-medium mt-1 ml-4 ${isDarkTheme ? "text-gray-400" : "text-gray-500"}`}>
                                ADMIN PANEL
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center w-full">
                        <div className="flex items-center justify-center w-full">
                            <span onClick={() => navigate("/")} className="cursor-pointer text-2xl font-bold bg-gradient-to-r from-amber-400 to-amber-600 bg-clip-text text-transparent">
                                OG
                            </span>
                        </div>

                        {/* Toggle Button below the header for collapsed view (kept as is) */}
                        <button
                            onClick={toggleSidebar}
                            className={`mt-4 rounded-full p-2 transition-all duration-200 ease-in-out ${isDarkTheme
                                ? "bg-gray-800 text-amber-400 hover:bg-gray-700"
                                : "bg-gray-200 text-amber-500 hover:bg-gray-300"
                                } hover:shadow-md`}
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>
                )}
            </div>

            {/* Menu Items */}
            <div className="flex-1 py-6 overflow-y-auto">
                <ul className="space-y-3 px-3">
                    {menuItems.map((item, index) => (
                        <li key={index}>
                            <NavLink
                                to={item.path}
                                end={item.path === "/admin"}
                                className={({ isActive }) =>
                                    `relative flex items-center ${collapsed ? "justify-center" : "justify-start px-4"
                                    } py-3 rounded-lg transition-all duration-300 ${isActive
                                        ? "bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-md"
                                        : isDarkTheme
                                            ? "text-gray-300 hover:bg-gray-800"
                                            : "text-gray-700 hover:bg-gray-200"
                                    }`
                                }
                                onMouseEnter={() => setHoverItem(index)}
                                onMouseLeave={() => setHoverItem(null)}
                            >
                                {({ isActive }) => (
                                    <>
                                        <div className={`${isActive ? "text-white" : ""} ${hoverItem === index && !isActive ? "text-amber-500" : ""}`}>
                                            {item.icon}
                                        </div>
                                        {!collapsed && (
                                            <span className={`ml-3 ${isActive ? "font-medium" : ""}`}>
                                                {item.name}
                                            </span>
                                        )}
                                        {/* Indicator dot for active item */}
                                        {isActive && collapsed && (
                                            <span className="absolute w-1.5 h-1.5 bg-white rounded-full -right-0.5"></span>
                                        )}
                                    </>
                                )}
                            </NavLink>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Theme Toggle Button in Sidebar */}
            <div className="p-4" title="Toggle Theme">
                <button
                    onClick={toggleTheme}
                    className={`flex items-center justify-center w-full p-2 rounded-lg transition-colors duration-200 ${isDarkTheme
                            ? "bg-gray-800 hover:bg-gray-700 text-yellow-300"
                            : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                        }`}
                >
                    {isDarkTheme ? <Sun size={23} /> : <Moon size={23} />}
                    {!collapsed && (
                        <span className="ml-3 text-sm font-medium">
                            {isDarkTheme ? "Light Mode" : "Dark Mode"}
                        </span>
                    )}
                </button>
            </div>


            {/* Admin badge above logout - only visible when not collapsed */}
            {!collapsed && (
                <div className="mx-4 mb-4 py-2 px-3 bg-gradient-to-r from-amber-100 to-amber-200 dark:from-gray-800 dark:to-gray-700 rounded-lg flex items-center">
                    <Shield size={16} className="text-amber-500" />
                    <span className="ml-2 text-xs font-medium text-amber-700 dark:text-amber-400">Admin Access</span>
                </div>
            )}

            {/* Logout Button */}
            <div className="p-4">
                <button
                    onClick={handleLogout}
                    className={`flex items-center ${collapsed ? "justify-center w-full" : "justify-start w-full px-4"
                        } py-3 rounded-lg transition-all duration-200 ${isDarkTheme
                            ? "text-gray-300 hover:bg-red-900/20 hover:text-red-400"
                            : "text-gray-700 hover:bg-red-100 hover:text-red-600"
                        }`}
                >
                    <LogOut size={20} />
                    {!collapsed && <span className="ml-3">Logout</span>}
                </button>
            </div>

            {/* Decorative element - bottom left corner accent */}
            <div className="absolute bottom-0 left-0 w-12 h-12 bg-gradient-to-tr from-amber-400 to-amber-700 opacity-10 rounded-tr-full pointer-events-none"></div>
        </div>
    );
};

export default Sidebar;